/**
 * Web3Auth Gateway (v11 — `@web3auth/modal`)
 *
 * The only module in the app that imports `@web3auth/*`. Builds the SDK once at
 * module load, then exposes login, logout and session reads to `AuthContext` and
 * `useBridgeLoginPageViewModel`.
 *
 * v11 replaced v9's four packages and its whole adapter model: there is no
 * `chainConfig`, no `EthereumPrivateKeyProvider`, no `configureAdapter()` and no
 * `ADAPTER_EVENTS` to listen on. Chains and connectors come from the dashboard
 * project behind the client id, and the session is read explicitly instead of
 * being pushed at us by an event.
 *
 * Two things did NOT move, deliberately:
 *
 * 1. The public surface — `init`, `connectWithGoogle`, `connectWithFacebook`,
 *    `logout`, `setOnLoginSuccessCallback`, `connected`, `getUserInfo`,
 *    `getExistingSession`, plus the two re-exported environment constants.
 * 2. `Web3AuthUserInfo`'s field names, which are still v9's. v11 renamed every
 *    identity claim; `toUserInfo()` below is the one place that rename exists, so
 *    `AuthContext`, `useAuthStore` and the bridge ViewModel compile unchanged.
 *
 * See `docs/architecture/features/web3auth-v11-migration/index.md`.
 */

import {
  AUTH_CONNECTION,
  CONNECTOR_EVENTS,
  CONNECTOR_STATUS,
  WALLET_CONNECTORS,
  Web3Auth
} from '@web3auth/modal';
import type { AUTH_CONNECTION_TYPE, UserInfo } from '@web3auth/modal';

import { web3AuthOptions } from './web3auth-config';
import { resolveWalletAddress } from './wallet-address';
import { isEmbedded, isBrowserSupportingPopupUxMode } from './environment';

// Re-exported so existing consumers don't have to move — the detection logic
// itself lives in environment.ts.
export { isEmbedded, isBrowserSupportingPopupUxMode };

/**
 * The login vocabulary SOKA already stores in `Users.typeOfLogin`.
 *
 * v9's `LOGIN_PROVIDER` and v11's `AUTH_CONNECTION` carry byte-identical values
 * for these two, which is what lets the column stay untouched across the major.
 */
const SOKA_TYPE_OF_LOGIN = {
  [AUTH_CONNECTION.GOOGLE]: 'google',
  [AUTH_CONNECTION.FACEBOOK]: 'facebook'
} as const;

export type SokaTypeOfLogin = (typeof SOKA_TYPE_OF_LOGIN)[keyof typeof SOKA_TYPE_OF_LOGIN];

/**
 * The user record the app passes around — still under v9's field names.
 *
 * Every rename from v11 (`authConnectionId`, `userId`, `authConnection`) is
 * undone by `toUserInfo()` before a value reaches this shape.
 */
export interface Web3AuthUserInfo {
  email?: string;
  name?: string;
  profileImage?: string;
  /** v11's `authConnectionId`. */
  verifier?: string;
  /** v11's `userId`. */
  verifierId?: string;
  /**
   * v11's `authConnection`, normalised to SOKA's vocabulary. ABSENT — not a
   * placeholder — when the connection is one we do not recognise.
   */
  typeOfLogin?: SokaTypeOfLogin;
  /** Read from the connection's Ethereum provider via `eth_accounts`. */
  wallet?: string;
  /** From `getAuthTokenInfo()`; v11's user record no longer carries it. */
  idToken?: string;
}

type LoginSuccessCallback = (user: Web3AuthUserInfo) => void;

/**
 * Whether this page load is the return leg of a redirect-mode login.
 *
 * The auth service sends the user back with its result in a `b64Params` hash
 * (or query) parameter, which the SDK strips once it has read it — so this is
 * read at module load, before `init()` gets the chance.
 */
function isRedirectLoginReturn(): boolean {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const query = new URLSearchParams(window.location.search);
  return hash.has('b64Params') || query.has('b64Params');
}

/**
 * The longest a redirect return waits for the SDK to rehydrate its session.
 *
 * Covers the project-config fetch plus the key reconstruction; past this the
 * login page is handed back to the user rather than left spinning.
 */
const REDIRECT_RESTORE_TIMEOUT_MS = 20_000;

/**
 * How long a redirect-mode login may keep the page before it counts as abandoned.
 *
 * `connectTo` settles only on CONNECTED/ERRORED, and in redirect mode neither fires:
 * the SDK expects the page to be gone. Android can hand the tap-triggered
 * facebook.com navigation to the Facebook app instead, leaving the tab in place
 * with a login that would otherwise spin forever.
 */
const REDIRECT_DEPARTURE_TIMEOUT_MS = 8_000;

/**
 * How long a login waits for the login connector after `init()` has resolved.
 *
 * `init()` resolves before the connector finishes setting up, and a `connectTo` in
 * that gap fails inside the SDK without ever settling. Past this, `connectTo` runs
 * anyway and fails the way it always did.
 */
const CONNECTOR_READY_TIMEOUT_MS = 15_000;

// Module-level initialization: the SDK must exist before the singleton below is
// constructed, and a redirect-mode return depends on init() starting at load.
let web3auth: Web3Auth | null = null;
let initPromise: Promise<void> | null = null;

// Skip Web3Auth initialization entirely in embedded contexts (webview/iframe).
// In embedded mode, authentication is handled via the session bridge flow
// which opens the device browser — Web3Auth runs there, not here.
if (isEmbedded) {
  console.warn('Embedded context detected — skipping Web3Auth init (bridge flow will be used)');
} else if (web3AuthOptions.clientId) {
  web3auth = new Web3Auth(web3AuthOptions);

  // Start init (async, but we don't await it here)
  initPromise = web3auth.init();
} else {
  console.warn('Web3Auth client ID not configured. Authentication will not work.');
}

/**
 * Resolves once the SDK has finished rehydrating a session — connected, failed,
 * or out of time. Never rejects: the session read that follows is the verdict.
 */
function waitForRehydration(auth: Web3Auth): Promise<void> {
  if (auth.connected) return Promise.resolve();

  return waitForAnyEvent(
    auth,
    [CONNECTOR_EVENTS.CONNECTED, CONNECTOR_EVENTS.REHYDRATION_ERROR, CONNECTOR_EVENTS.ERRORED],
    REDIRECT_RESTORE_TIMEOUT_MS
  );
}

/** Resolves once the SDK's login connector has left `NOT_READY`, or out of time. Never rejects. */
function waitForConnectorReady(auth: Web3Auth): Promise<void> {
  if (auth.status !== CONNECTOR_STATUS.NOT_READY) return Promise.resolve();

  return waitForAnyEvent(
    auth,
    [CONNECTOR_EVENTS.READY, CONNECTOR_EVENTS.CONNECTED, CONNECTOR_EVENTS.ERRORED],
    CONNECTOR_READY_TIMEOUT_MS
  );
}

/** Resolves on the first of `events` or after `timeoutMs`, removing its listeners either way. */
function waitForAnyEvent(
  auth: Web3Auth,
  events: Parameters<Web3Auth['on']>[0][],
  timeoutMs: number
): Promise<void> {
  return new Promise((resolve) => {
    const settle = (): void => {
      clearTimeout(timeout);
      for (const event of events) auth.off(event, settle);
      resolve();
    };
    const timeout = setTimeout(settle, timeoutMs);
    for (const event of events) auth.on(event, settle);
  });
}

/**
 * Rejects once a redirect-mode login has evidently not left the page: the timeout
 * passes before `pagehide`, or the tab becomes visible again after being hidden (the
 * user is back from the app that took the navigation, or from a back-forward cache).
 */
function watchRedirectDeparture(): { stayed: Promise<never>; stop: () => void } {
  let stop = (): void => undefined;

  const stayed = new Promise<never>((_, reject) => {
    let wasHidden = false;
    const fail = (): void => {
      stop();
      reject(new Error('redirect login did not leave the page'));
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') wasHidden = true;
      else if (wasHidden) fail();
    };
    // A page that is leaving must not time out mid-navigation on a slow network.
    const onPageHide = (): void => {
      clearTimeout(timeout);
    };
    const timeout = setTimeout(fail, REDIRECT_DEPARTURE_TIMEOUT_MS);

    stop = () => {
      clearTimeout(timeout);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
  });

  return { stayed, stop };
}

/** Resolves a v11 connection to SOKA's vocabulary, or to nothing at all. */
function normaliseTypeOfLogin(
  authConnection: Partial<UserInfo>['authConnection']
): SokaTypeOfLogin | undefined {
  if (authConnection === undefined) return undefined;

  const known: Record<string, SokaTypeOfLogin | undefined> = SOKA_TYPE_OF_LOGIN;
  return known[authConnection];
}

/**
 * The v11 → v9 claim rename. This function is the only place it happens.
 *
 * `typeOfLogin` is spread conditionally on purpose: an unrecognised connection
 * must leave the key ABSENT so the register call sites can reject it loudly,
 * rather than persisting a substituted literal into `Users.typeOfLogin`.
 */
function toUserInfo(info: Partial<UserInfo>, wallet?: string, idToken?: string): Web3AuthUserInfo {
  const typeOfLogin = normaliseTypeOfLogin(info.authConnection);

  return {
    email: info.email,
    name: info.name,
    profileImage: info.profileImage,
    verifier: info.authConnectionId,
    verifierId: info.userId,
    wallet,
    idToken,
    ...(typeOfLogin === undefined ? {} : { typeOfLogin })
  };
}

/**
 * Flattens an error and its whole `cause` chain into one readable line.
 *
 * The SDK wraps a failed login in an opaque error whose real reason lives one
 * or two `cause` levels down — logging `error.message` alone prints the
 * wrapper and discards the answer. This is the one place that gets unwrapped,
 * so every failure log actually says something.
 */
function describeLoginFailure(error: unknown): string {
  const layers: string[] = [];
  const seen = new Set<unknown>();

  for (let current = error; current instanceof Error && !seen.has(current); ) {
    seen.add(current);
    layers.push(`${current.name}: ${current.message}`);
    current = current.cause;
  }

  return layers.length > 0 ? layers.join(' ← caused by ') : String(error);
}

/**
 * The pause before each retry of a call that reaches Web3Auth's citadel service.
 *
 * 200 ms, 400 ms, 800 ms — ~1.4 s total across three attempts. This rides out
 * the citadel's own write lag on a first login (its record can be unreadable
 * for a few hundred milliseconds right after the login that created it), not a
 * network outage; extending the budget would turn a failed login into a
 * frozen login screen.
 */
const CITADEL_RETRY_DELAYS_MS = [200, 400, 800];

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Runs `call`, retrying a rejection on the schedule above before giving up on it. */
async function withCitadelRetry<T>(call: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await call();
    } catch (error) {
      if (attempt >= CITADEL_RETRY_DELAYS_MS.length) throw error;
      await pause(CITADEL_RETRY_DELAYS_MS[attempt]);
    }
  }
}

class Web3AuthGateway {
  private onLoginSuccessCallback: LoginSuccessCallback | null = null;

  /**
   * The one announcement of an SDK-restored session, shared by every trigger.
   *
   * `init()` is called by both `AuthContext` and the bridge ViewModel, and the
   * SDK's `CONNECTED` event can race either of them — sharing the promise means
   * the session is announced once and every trigger learns the same outcome.
   */
  private restoredAnnouncement: Promise<boolean> | null = null;

  /** True while `connect()` owns the login, so its `CONNECTED` event is not announced twice. */
  private isConnecting = false;

  /** Cleared once the redirect-return restore has settled, so it is only waited on once. */
  private redirectRestorePending = web3auth !== null && isRedirectLoginReturn();

  constructor() {
    // v11's init() resolves BEFORE the auth connector finishes rehydrating a
    // redirect-mode return (Safari) or a cached session: connectors are
    // initialised from an un-awaited event handler inside the SDK. The session
    // only becomes readable when the SDK emits CONNECTED, so listen for it.
    web3auth?.on(CONNECTOR_EVENTS.CONNECTED, () => {
      void this.announceRestoredSession();
    });
  }

  /**
   * Waits for the module-level init, then announces a session that was already
   * restored — the redirect-mode return, and a reload with a live session.
   *
   * On a redirect return it also waits for the SDK to finish rehydrating, so a
   * caller can keep its loading state up for the whole restore. Resolves `true`
   * when a restored session was announced, `false` otherwise.
   */
  async init(): Promise<boolean> {
    if (!initPromise || !web3auth) return false;
    await initPromise;

    if (this.redirectRestorePending) {
      await waitForRehydration(web3auth);
    }

    try {
      return await this.announceRestoredSession();
    } finally {
      this.redirectRestorePending = false;
    }
  }

  /** Whether this page load is a redirect-mode login the SDK has yet to restore. */
  get isRestoringRedirect(): boolean {
    return this.redirectRestorePending;
  }

  /** Whether a login here leaves the page (redirect mode) instead of opening a popup. */
  get usesRedirectLogin(): boolean {
    return web3auth !== null && !isBrowserSupportingPopupUxMode;
  }

  async connectWithGoogle(): Promise<Web3AuthUserInfo> {
    return this.connect(AUTH_CONNECTION.GOOGLE);
  }

  async connectWithFacebook(): Promise<Web3AuthUserInfo> {
    return this.connect(AUTH_CONNECTION.FACEBOOK);
  }

  async logout(): Promise<void> {
    if (web3auth?.connected) {
      await web3auth.logout();
    }
  }

  setOnLoginSuccessCallback(callback: LoginSuccessCallback): void {
    this.onLoginSuccessCallback = callback;
  }

  get connected(): boolean {
    return web3auth?.connected ?? false;
  }

  async getUserInfo(): Promise<Web3AuthUserInfo | undefined> {
    if (!web3auth) return undefined;
    return toUserInfo(await web3auth.getUserInfo());
  }

  /**
   * Get existing session user info + wallet if already connected.
   * Returns null if no active session. Does NOT trigger a new login.
   */
  async getExistingSession(): Promise<Web3AuthUserInfo | null> {
    try {
      return await this.readSession();
    } catch {
      return null;
    }
  }

  /**
   * Hands a session the SDK restored on its own (no `connect()` call) to the
   * login-success callback, exactly once.
   *
   * Waits for a registered callback: a `CONNECTED` event that beats
   * `AuthContext`'s mount is picked up by its later `init()` call instead.
   */
  private announceRestoredSession(): Promise<boolean> {
    if (this.restoredAnnouncement) return this.restoredAnnouncement;

    const onLoginSuccess = this.onLoginSuccessCallback;
    if (this.isConnecting || !onLoginSuccess || !web3auth?.connected) {
      return Promise.resolve(false);
    }

    const announcement = this.getExistingSession().then((session) => {
      if (!session) {
        // Nothing was announced, so a later trigger may still try.
        this.restoredAnnouncement = null;
        return false;
      }
      onLoginSuccess(session);
      return true;
    });
    this.restoredAnnouncement = announcement;
    return announcement;
  }

  /**
   * Every social login lands here.
   *
   * The connect options carry the connection and nothing else. Naming an
   * `authConnectionId` would target a different dashboard connection and so
   * re-derive every existing user's wallet address; sending none selects the
   * project default, which is the shared group v9 derived from. `mfaLevel` is
   * stated once in `web3AuthOptions`, never per call.
   *
   * A `connectTo` rejection is held, not propagated: the SDK's own bookkeeping
   * read can 404 on a brand-new user's first login even though the login itself
   * completed, so the rejection is not the verdict — the session read is. A
   * session that reads back means the login worked and the rejection is
   * ignored; no session means the rejection was real and is rethrown unchanged.
   */
  private async connect(authConnection: AUTH_CONNECTION_TYPE): Promise<Web3AuthUserInfo> {
    await this.ensureInitialized();

    if (!web3auth) {
      throw new Error('Web3Auth not initialized');
    }

    let connectFailure: unknown;
    let failed = false;
    let session: Web3AuthUserInfo | null;
    const departure = this.usesRedirectLogin ? watchRedirectDeparture() : undefined;
    this.isConnecting = true;
    try {
      try {
        const connecting = web3auth.connectTo(WALLET_CONNECTORS.AUTH, { authConnection });
        await (departure ? Promise.race([connecting, departure.stayed]) : connecting);
      } catch (error) {
        failed = true;
        connectFailure = error;
        console.error('Web3Auth login failed:', describeLoginFailure(error));
      } finally {
        departure?.stop();
      }

      session = await this.readSession();
    } finally {
      this.isConnecting = false;
    }

    if (session) {
      this.onLoginSuccessCallback?.(session);
      return session;
    }

    if (failed) {
      throw connectFailure;
    }

    throw new Error(`${authConnection} login connected without producing a session`);
  }

  /**
   * The one session read: wallet, profile and identity token off the live handle.
   *
   * v11 kept a `provider` property on the SDK but left it a SETTER ONLY — reading
   * it back yields `undefined` with no error, which would make every session look
   * disconnected. `connection?.ethereumProvider` is where the EIP-1193 handle
   * lives now, and it is the only handle this gateway ever touches.
   *
   * `getUserInfo()` and `getAuthTokenInfo()` both reach the same citadel service,
   * which can still be catching up on a brand-new user's record right after the
   * login that created it — so both go through `withCitadelRetry`.
   */
  private async readSession(): Promise<Web3AuthUserInfo | null> {
    const auth = web3auth;
    if (!auth?.connected) return null;

    const ethereumProvider = auth.connection?.ethereumProvider;
    if (!ethereumProvider) return null;

    const wallet = await resolveWalletAddress(ethereumProvider);
    const info = await withCitadelRetry(() => auth.getUserInfo());
    const { idToken } = await withCitadelRetry(() => auth.getAuthTokenInfo());

    return toUserInfo(info, wallet, idToken);
  }

  private async ensureInitialized(): Promise<void> {
    if (initPromise) {
      await initPromise;
    }

    if (!web3auth) {
      throw new Error('Web3Auth not initialized');
    }

    await waitForConnectorReady(web3auth);
  }
}

// Singleton instance
const web3authGateway = new Web3AuthGateway();
export default web3authGateway;
